/**
 * Configurable dropdown search/display fields.
 * Defaults mirror current hard-coded behavior so unconfigured companies see no change.
 */

export const DROPDOWN_FIELD_CONFIG_KEY = 'Dropdown.FieldConfig'

export type DropdownTypeKey =
  | 'customer'
  | 'patient'
  | 'vendor'
  | 'contact'
  | 'account'
  | 'cashAccount'
  | 'item'
  | 'itemUnit'
  | 'unit'
  | 'category'
  | 'tax'
  | 'store'
  | 'branch'
  | 'costCenter'
  | 'currency'
  | 'paymentMethod'
  | 'salesRep'
  | 'operationType'

export interface DropdownTypeFieldConfig {
  /** Fields used when filtering the list by the search box */
  searchFields: string[]
  /** Fields shown in the closed trigger (joined) */
  triggerFields: string[]
  /** Fields shown for each option in the open list (first = primary, rest = secondary) */
  listFields: string[]
}

export type DropdownFieldConfigMap = Partial<Record<DropdownTypeKey, DropdownTypeFieldConfig>>

export interface DropdownFieldDefinition {
  key: string
  labelAr: string
  labelEn: string
  /** Extract display/search string from a raw API row */
  getValue: (item: any) => string
  /** Also match by digits-only (phones, tax ids) */
  digitNormalize?: boolean
  searchable?: boolean
  displayable?: boolean
}

export interface DropdownTypeDefinition {
  key: DropdownTypeKey
  labelAr: string
  labelEn: string
  fields: DropdownFieldDefinition[]
  defaults: DropdownTypeFieldConfig
}

function pick(item: any, ...keys: string[]): string {
  if (!item) return ''
  for (const key of keys) {
    const v = item[key]
    if (v != null && String(v).trim() !== '') return String(v)
  }
  return ''
}

function localizedName(item: any, language: string): string {
  if (language === 'ar') {
    return pick(item, 'name', 'Name', 'attName', 'AttName', 'description', 'Description') ||
      pick(item, 'nameEn', 'NameEn', 'nameEN', 'NameEN', 'attNameEN', 'AttNameEN', 'descriptionEn', 'DescriptionEn')
  }
  return pick(item, 'nameEn', 'NameEn', 'nameEN', 'NameEN', 'attNameEN', 'AttNameEN', 'descriptionEn', 'DescriptionEn') ||
    pick(item, 'name', 'Name', 'attName', 'AttName', 'description', 'Description')
}

export const DROPDOWN_TYPE_DEFINITIONS: DropdownTypeDefinition[] = [
  {
    key: 'customer',
    labelAr: 'العملاء',
    labelEn: 'Customers',
    defaults: {
      searchFields: ['name', 'nameEn', 'nbrTax', 'mobile', 'phone2', 'whatsAppNumber'],
      triggerFields: ['name'],
      listFields: ['name', 'mobile', 'phone2', 'nbrTax'],
    },
    fields: [
      { key: 'name', labelAr: 'الاسم', labelEn: 'Name', getValue: (i) => pick(i, 'name', 'Name'), searchable: true, displayable: true },
      { key: 'nameEn', labelAr: 'الاسم الإنجليزي', labelEn: 'English Name', getValue: (i) => pick(i, 'nameEn', 'NameEn'), searchable: true, displayable: true },
      { key: 'nbrTax', labelAr: 'الرقم الضريبي', labelEn: 'Tax Number', getValue: (i) => pick(i, 'nbrTax', 'NBRTax'), searchable: true, displayable: true, digitNormalize: true },
      { key: 'mobile', labelAr: 'الجوال', labelEn: 'Mobile', getValue: (i) => pick(i, 'mobile', 'Mobile'), searchable: true, displayable: true, digitNormalize: true },
      { key: 'phone2', labelAr: 'هاتف 2', labelEn: 'Phone 2', getValue: (i) => pick(i, 'phone2', 'Phone2'), searchable: true, displayable: true, digitNormalize: true },
      { key: 'whatsAppNumber', labelAr: 'واتساب', labelEn: 'WhatsApp', getValue: (i) => pick(i, 'whatsAppNumber', 'WhatsAppNumber'), searchable: true, displayable: true, digitNormalize: true },
      { key: 'code', labelAr: 'الرمز', labelEn: 'Code', getValue: (i) => pick(i, 'code', 'Code', 'customerCode', 'CustomerCode'), searchable: true, displayable: true },
      { key: 'id', labelAr: 'المعرف', labelEn: 'ID', getValue: (i) => pick(i, 'id', 'ID'), searchable: true, displayable: true },
    ],
  },
  {
    key: 'patient',
    labelAr: 'المرضى',
    labelEn: 'Patients',
    defaults: {
      searchFields: ['name', 'nameEn', 'mobile', 'phone2', 'whatsAppNumber', 'patientSerial'],
      triggerFields: ['name'],
      listFields: ['name', 'patientSerial', 'mobile'],
    },
    fields: [
      { key: 'name', labelAr: 'الاسم', labelEn: 'Name', getValue: (i) => pick(i, 'name', 'Name'), searchable: true, displayable: true },
      { key: 'nameEn', labelAr: 'الاسم الإنجليزي', labelEn: 'English Name', getValue: (i) => pick(i, 'nameEn', 'NameEn'), searchable: true, displayable: true },
      { key: 'patientSerial', labelAr: 'رقم الملف', labelEn: 'File No.', getValue: (i) => pick(i, 'patientSerial', 'PatientSerial'), searchable: true, displayable: true },
      { key: 'mobile', labelAr: 'الجوال', labelEn: 'Mobile', getValue: (i) => pick(i, 'mobile', 'Mobile'), searchable: true, displayable: true, digitNormalize: true },
      { key: 'phone2', labelAr: 'هاتف 2', labelEn: 'Phone 2', getValue: (i) => pick(i, 'phone2', 'Phone2'), searchable: true, displayable: true, digitNormalize: true },
      { key: 'whatsAppNumber', labelAr: 'واتساب', labelEn: 'WhatsApp', getValue: (i) => pick(i, 'whatsAppNumber', 'WhatsAppNumber'), searchable: true, displayable: true, digitNormalize: true },
      { key: 'nbrTax', labelAr: 'الرقم الضريبي', labelEn: 'Tax Number', getValue: (i) => pick(i, 'nbrTax', 'NBRTax'), searchable: true, displayable: true, digitNormalize: true },
    ],
  },
  {
    key: 'vendor',
    labelAr: 'الموردون',
    labelEn: 'Vendors',
    defaults: {
      searchFields: ['name', 'nameEn', 'nbrTax'],
      triggerFields: ['name'],
      listFields: ['name', 'nbrTax'],
    },
    fields: [
      { key: 'name', labelAr: 'الاسم', labelEn: 'Name', getValue: (i) => pick(i, 'name', 'Name'), searchable: true, displayable: true },
      { key: 'nameEn', labelAr: 'الاسم الإنجليزي', labelEn: 'English Name', getValue: (i) => pick(i, 'nameEn', 'NameEn'), searchable: true, displayable: true },
      { key: 'nbrTax', labelAr: 'الرقم الضريبي', labelEn: 'Tax Number', getValue: (i) => pick(i, 'nbrTax', 'NBRTax'), searchable: true, displayable: true, digitNormalize: true },
      { key: 'mobile', labelAr: 'الجوال', labelEn: 'Mobile', getValue: (i) => pick(i, 'mobile', 'Mobile'), searchable: true, displayable: true, digitNormalize: true },
      { key: 'code', labelAr: 'الرمز', labelEn: 'Code', getValue: (i) => pick(i, 'code', 'Code'), searchable: true, displayable: true },
      { key: 'id', labelAr: 'المعرف', labelEn: 'ID', getValue: (i) => pick(i, 'id', 'ID'), searchable: true, displayable: true },
    ],
  },
  {
    key: 'contact',
    labelAr: 'جهات الاتصال',
    labelEn: 'Contacts',
    defaults: {
      searchFields: ['name'],
      triggerFields: ['name'],
      listFields: ['name'],
    },
    fields: [
      { key: 'name', labelAr: 'الاسم', labelEn: 'Name', getValue: (i) => pick(i, 'name', 'Name'), searchable: true, displayable: true },
      { key: 'nameEn', labelAr: 'الاسم الإنجليزي', labelEn: 'English Name', getValue: (i) => pick(i, 'nameEn', 'NameEn'), searchable: true, displayable: true },
      { key: 'nbrTax', labelAr: 'الرقم الضريبي', labelEn: 'Tax Number', getValue: (i) => pick(i, 'nbrTax', 'NBRTax'), searchable: true, displayable: true, digitNormalize: true },
      { key: 'mobile', labelAr: 'الجوال', labelEn: 'Mobile', getValue: (i) => pick(i, 'mobile', 'Mobile'), searchable: true, displayable: true, digitNormalize: true },
      { key: 'id', labelAr: 'المعرف', labelEn: 'ID', getValue: (i) => pick(i, 'id', 'ID'), searchable: true, displayable: true },
    ],
  },
  {
    key: 'account',
    labelAr: 'الحسابات',
    labelEn: 'Accounts',
    defaults: {
      searchFields: ['name', 'nameEn', 'id', 'cachedNumber'],
      triggerFields: ['name'],
      listFields: ['cachedNumber', 'name'],
    },
    fields: [
      { key: 'name', labelAr: 'الاسم', labelEn: 'Name', getValue: (i) => pick(i, 'name', 'Name'), searchable: true, displayable: true },
      { key: 'nameEn', labelAr: 'الاسم الإنجليزي', labelEn: 'English Name', getValue: (i) => pick(i, 'nameEn', 'NameEn'), searchable: true, displayable: true },
      { key: 'cachedNumber', labelAr: 'رقم الحساب', labelEn: 'Account No.', getValue: (i) => pick(i, 'cachedNumber', 'CachedNumber', 'accountNumber', 'AccountNumber'), searchable: true, displayable: true },
      { key: 'id', labelAr: 'المعرف', labelEn: 'ID', getValue: (i) => pick(i, 'id', 'ID'), searchable: true, displayable: true },
    ],
  },
  {
    key: 'cashAccount',
    labelAr: 'حسابات الخزينة',
    labelEn: 'Cash Accounts',
    defaults: {
      searchFields: ['name', 'nameEn', 'id', 'cachedNumber'],
      triggerFields: ['name'],
      listFields: ['name', 'cachedNumber'],
    },
    fields: [
      { key: 'name', labelAr: 'الاسم', labelEn: 'Name', getValue: (i) => pick(i, 'name', 'Name'), searchable: true, displayable: true },
      { key: 'nameEn', labelAr: 'الاسم الإنجليزي', labelEn: 'English Name', getValue: (i) => pick(i, 'nameEn', 'NameEn'), searchable: true, displayable: true },
      { key: 'cachedNumber', labelAr: 'رقم الحساب', labelEn: 'Account No.', getValue: (i) => pick(i, 'cachedNumber', 'CachedNumber'), searchable: true, displayable: true },
      { key: 'id', labelAr: 'المعرف', labelEn: 'ID', getValue: (i) => pick(i, 'id', 'ID'), searchable: true, displayable: true },
    ],
  },
  {
    key: 'item',
    labelAr: 'الأصناف',
    labelEn: 'Items',
    defaults: {
      searchFields: ['barcode', 'name', 'nameEn', 'id'],
      triggerFields: ['name'],
      listFields: ['name', 'barcode', 'defaultPrice'],
    },
    fields: [
      { key: 'name', labelAr: 'الاسم', labelEn: 'Name', getValue: (i) => pick(i, 'name', 'Name'), searchable: true, displayable: true },
      { key: 'nameEn', labelAr: 'الاسم الإنجليزي', labelEn: 'English Name', getValue: (i) => pick(i, 'nameEn', 'NameEn'), searchable: true, displayable: true },
      { key: 'barcode', labelAr: 'الباركود', labelEn: 'Barcode', getValue: (i) => pick(i, 'barcode', 'Barcode'), searchable: true, displayable: true },
      { key: 'codeItem', labelAr: 'رمز الصنف', labelEn: 'Item Code', getValue: (i) => pick(i, 'codeItem', 'CodeItem'), searchable: true, displayable: true },
      { key: 'defaultPrice', labelAr: 'السعر', labelEn: 'Price', getValue: (i) => pick(i, 'defaultPrice', 'DefaultPrice', 'price', 'Price'), searchable: false, displayable: true },
      { key: 'id', labelAr: 'المعرف', labelEn: 'ID', getValue: (i) => pick(i, 'id', 'ID'), searchable: true, displayable: true },
    ],
  },
  {
    key: 'itemUnit',
    labelAr: 'وحدات الصنف',
    labelEn: 'Item Units',
    defaults: {
      searchFields: ['name', 'nameEn'],
      triggerFields: ['name'],
      listFields: ['name', 'ratio'],
    },
    fields: [
      { key: 'name', labelAr: 'الاسم', labelEn: 'Name', getValue: (i) => pick(i?.unit, 'name', 'Name', 'attName') || pick(i, 'name', 'Name', 'attName'), searchable: true, displayable: true },
      { key: 'nameEn', labelAr: 'الاسم الإنجليزي', labelEn: 'English Name', getValue: (i) => pick(i?.unit, 'nameEn', 'NameEn', 'attNameEN') || pick(i, 'nameEn', 'NameEn', 'attNameEN'), searchable: true, displayable: true },
      { key: 'ratio', labelAr: 'المعامل', labelEn: 'Ratio', getValue: (i) => pick(i, 'ratio', 'Ratio'), searchable: false, displayable: true },
    ],
  },
  {
    key: 'unit',
    labelAr: 'الوحدات',
    labelEn: 'Units',
    defaults: {
      searchFields: ['attName', 'attNameEN'],
      triggerFields: ['attName'],
      listFields: ['attName'],
    },
    fields: [
      { key: 'attName', labelAr: 'الاسم', labelEn: 'Name', getValue: (i) => pick(i, 'attName', 'AttName'), searchable: true, displayable: true },
      { key: 'attNameEN', labelAr: 'الاسم الإنجليزي', labelEn: 'English Name', getValue: (i) => pick(i, 'attNameEN', 'AttNameEN'), searchable: true, displayable: true },
      { key: 'id', labelAr: 'المعرف', labelEn: 'ID', getValue: (i) => pick(i, 'id', 'ID'), searchable: true, displayable: true },
    ],
  },
  {
    key: 'category',
    labelAr: 'الفئات',
    labelEn: 'Categories',
    defaults: {
      searchFields: ['name'],
      triggerFields: ['name'],
      listFields: ['name'],
    },
    fields: [
      { key: 'name', labelAr: 'الاسم', labelEn: 'Name', getValue: (i) => pick(i, 'name', 'Name'), searchable: true, displayable: true },
      { key: 'id', labelAr: 'المعرف', labelEn: 'ID', getValue: (i) => pick(i, 'id', 'ID'), searchable: true, displayable: true },
    ],
  },
  {
    key: 'tax',
    labelAr: 'الضرائب',
    labelEn: 'Taxes',
    defaults: {
      searchFields: ['name', 'nameEn', 'rate'],
      triggerFields: ['name', 'rate'],
      listFields: ['name', 'rate'],
    },
    fields: [
      { key: 'name', labelAr: 'الاسم', labelEn: 'Name', getValue: (i) => pick(i, 'name', 'Name'), searchable: true, displayable: true },
      { key: 'nameEn', labelAr: 'الاسم الإنجليزي', labelEn: 'English Name', getValue: (i) => pick(i, 'nameEn', 'NameEn'), searchable: true, displayable: true },
      { key: 'rate', labelAr: 'النسبة', labelEn: 'Rate', getValue: (i) => pick(i, 'rate', 'Rate', 'percentage', 'Percentage', 'percentageValue', 'PercentageValue'), searchable: true, displayable: true },
      { key: 'id', labelAr: 'المعرف', labelEn: 'ID', getValue: (i) => pick(i, 'id', 'ID'), searchable: true, displayable: true },
    ],
  },
  {
    key: 'store',
    labelAr: 'المخازن',
    labelEn: 'Stores',
    defaults: {
      searchFields: ['name'],
      triggerFields: ['name'],
      listFields: ['name'],
    },
    fields: [
      { key: 'name', labelAr: 'الاسم', labelEn: 'Name', getValue: (i) => pick(i, 'name', 'Name'), searchable: true, displayable: true },
      { key: 'nameEn', labelAr: 'الاسم الإنجليزي', labelEn: 'English Name', getValue: (i) => pick(i, 'nameEn', 'NameEn'), searchable: true, displayable: true },
      { key: 'id', labelAr: 'المعرف', labelEn: 'ID', getValue: (i) => pick(i, 'id', 'ID'), searchable: true, displayable: true },
    ],
  },
  {
    key: 'branch',
    labelAr: 'الفروع',
    labelEn: 'Branches',
    defaults: {
      searchFields: ['name', 'id'],
      triggerFields: ['name'],
      listFields: ['name'],
    },
    fields: [
      { key: 'name', labelAr: 'الاسم', labelEn: 'Name', getValue: (i) => pick(i, 'name', 'Name'), searchable: true, displayable: true },
      { key: 'nameEn', labelAr: 'الاسم الإنجليزي', labelEn: 'English Name', getValue: (i) => pick(i, 'nameEn', 'NameEn'), searchable: true, displayable: true },
      { key: 'id', labelAr: 'المعرف', labelEn: 'ID', getValue: (i) => pick(i, 'id', 'ID'), searchable: true, displayable: true },
    ],
  },
  {
    key: 'costCenter',
    labelAr: 'مراكز التكلفة',
    labelEn: 'Cost Centers',
    defaults: {
      searchFields: ['name', 'nameEn', 'id'],
      triggerFields: ['name'],
      listFields: ['name'],
    },
    fields: [
      { key: 'name', labelAr: 'الاسم', labelEn: 'Name', getValue: (i) => pick(i, 'name', 'Name'), searchable: true, displayable: true },
      { key: 'nameEn', labelAr: 'الاسم الإنجليزي', labelEn: 'English Name', getValue: (i) => pick(i, 'nameEn', 'NameEn'), searchable: true, displayable: true },
      { key: 'id', labelAr: 'المعرف', labelEn: 'ID', getValue: (i) => pick(i, 'id', 'ID'), searchable: true, displayable: true },
    ],
  },
  {
    key: 'currency',
    labelAr: 'العملات',
    labelEn: 'Currencies',
    defaults: {
      searchFields: ['name', 'nameEn', 'id', 'symbol'],
      triggerFields: ['name', 'symbol'],
      listFields: ['name', 'symbol'],
    },
    fields: [
      { key: 'name', labelAr: 'الاسم', labelEn: 'Name', getValue: (i) => pick(i, 'name', 'Name'), searchable: true, displayable: true },
      { key: 'nameEn', labelAr: 'الاسم الإنجليزي', labelEn: 'English Name', getValue: (i) => pick(i, 'nameEn', 'NameEn'), searchable: true, displayable: true },
      { key: 'symbol', labelAr: 'الرمز', labelEn: 'Symbol', getValue: (i) => pick(i, 'symbol', 'Symbol'), searchable: true, displayable: true },
      { key: 'id', labelAr: 'المعرف', labelEn: 'ID', getValue: (i) => pick(i, 'id', 'ID'), searchable: true, displayable: true },
    ],
  },
  {
    key: 'paymentMethod',
    labelAr: 'طرق الدفع',
    labelEn: 'Payment Methods',
    defaults: {
      searchFields: ['name', 'nameEn', 'id'],
      triggerFields: ['name'],
      listFields: ['name'],
    },
    fields: [
      { key: 'name', labelAr: 'الاسم', labelEn: 'Name', getValue: (i) => pick(i, 'name', 'Name'), searchable: true, displayable: true },
      { key: 'nameEn', labelAr: 'الاسم الإنجليزي', labelEn: 'English Name', getValue: (i) => pick(i, 'nameEn', 'NameEn'), searchable: true, displayable: true },
      { key: 'id', labelAr: 'المعرف', labelEn: 'ID', getValue: (i) => pick(i, 'id', 'ID'), searchable: true, displayable: true },
    ],
  },
  {
    key: 'salesRep',
    labelAr: 'مندوبو المبيعات',
    labelEn: 'Sales Reps',
    defaults: {
      searchFields: ['name', 'code'],
      triggerFields: ['name'],
      listFields: ['name', 'code'],
    },
    fields: [
      { key: 'name', labelAr: 'الاسم', labelEn: 'Name', getValue: (i) => pick(i, 'name', 'Name'), searchable: true, displayable: true },
      { key: 'nameEn', labelAr: 'الاسم الإنجليزي', labelEn: 'English Name', getValue: (i) => pick(i, 'nameEn', 'NameEn', 'nameEN', 'NameEN'), searchable: true, displayable: true },
      { key: 'code', labelAr: 'الرمز', labelEn: 'Code', getValue: (i) => pick(i, 'code', 'Code'), searchable: true, displayable: true },
      { key: 'id', labelAr: 'المعرف', labelEn: 'ID', getValue: (i) => pick(i, 'id', 'ID'), searchable: true, displayable: true },
    ],
  },
  {
    key: 'operationType',
    labelAr: 'أنواع العمليات',
    labelEn: 'Operation Types',
    defaults: {
      searchFields: ['description', 'descriptionEn'],
      triggerFields: ['description'],
      listFields: ['description'],
    },
    fields: [
      { key: 'description', labelAr: 'الوصف', labelEn: 'Description', getValue: (i) => pick(i, 'description', 'Description'), searchable: true, displayable: true },
      { key: 'descriptionEn', labelAr: 'الوصف الإنجليزي', labelEn: 'English Description', getValue: (i) => pick(i, 'descriptionEn', 'DescriptionEn'), searchable: true, displayable: true },
      { key: 'id', labelAr: 'المعرف', labelEn: 'ID', getValue: (i) => pick(i, 'id', 'ID'), searchable: true, displayable: true },
    ],
  },
]

const definitionByKey = Object.fromEntries(
  DROPDOWN_TYPE_DEFINITIONS.map((d) => [d.key, d])
) as Record<DropdownTypeKey, DropdownTypeDefinition>

export function getDropdownTypeDefinition(type: DropdownTypeKey): DropdownTypeDefinition {
  return definitionByKey[type]
}

export function getDefaultDropdownFieldConfig(): DropdownFieldConfigMap {
  const map: DropdownFieldConfigMap = {}
  for (const def of DROPDOWN_TYPE_DEFINITIONS) {
    map[def.key] = { ...def.defaults, searchFields: [...def.defaults.searchFields], triggerFields: [...def.defaults.triggerFields], listFields: [...def.defaults.listFields] }
  }
  return map
}

export function mergeDropdownFieldConfig(saved?: DropdownFieldConfigMap | null): DropdownFieldConfigMap {
  const defaults = getDefaultDropdownFieldConfig()
  if (!saved || typeof saved !== 'object') return defaults

  const merged: DropdownFieldConfigMap = { ...defaults }
  for (const def of DROPDOWN_TYPE_DEFINITIONS) {
    const incoming = saved[def.key]
    if (!incoming) continue
    const allowed = new Set(def.fields.map((f) => f.key))
    const filterKeys = (keys: string[] | undefined, fallback: string[]) => {
      const cleaned = (keys || []).filter((k) => allowed.has(k))
      return cleaned.length > 0 ? cleaned : fallback
    }
    merged[def.key] = {
      searchFields: filterKeys(incoming.searchFields, def.defaults.searchFields),
      triggerFields: filterKeys(incoming.triggerFields, def.defaults.triggerFields),
      listFields: filterKeys(incoming.listFields, def.defaults.listFields),
    }
  }
  return merged
}

export function parseDropdownFieldConfig(raw: unknown): DropdownFieldConfigMap {
  if (raw == null) return getDefaultDropdownFieldConfig()
  if (typeof raw === 'string') {
    try {
      return mergeDropdownFieldConfig(JSON.parse(raw))
    } catch {
      return getDefaultDropdownFieldConfig()
    }
  }
  if (typeof raw === 'object') {
    return mergeDropdownFieldConfig(raw as DropdownFieldConfigMap)
  }
  return getDefaultDropdownFieldConfig()
}

function digitsOnly(s: string): string {
  return s.replace(/\D/g, '')
}

function fieldDefsFor(type: DropdownTypeKey, keys: string[] | undefined | null): DropdownFieldDefinition[] {
  const def = definitionByKey[type]
  if (!def?.fields) return []
  const byKey = Object.fromEntries(def.fields.map((f) => [f.key, f]))
  return (keys ?? []).map((k) => byKey[k]).filter(Boolean)
}

/** Resolve a field value with language-aware name when key is `name` / `attName` / `description`. */
function resolveFieldValue(field: DropdownFieldDefinition, item: any, language: string): string {
  if (field.key === 'name' || field.key === 'attName' || field.key === 'description') {
    const localized = localizedName(item, language)
    if (localized) return localized
  }
  return field.getValue(item)
}

export function matchConfiguredFields(
  item: any,
  query: string,
  type: DropdownTypeKey,
  searchFields: string[],
  language: string = 'ar'
): boolean {
  const t = query.trim()
  if (!t) return true
  const lower = t.toLowerCase()
  const fields = fieldDefsFor(type, searchFields)
  if (fields.length === 0) return true

  for (const field of fields) {
    const value = resolveFieldValue(field, item, language)
    if (value && value.toLowerCase().includes(lower)) return true
  }

  const dSearch = digitsOnly(t)
  if (dSearch.length >= 2) {
    for (const field of fields) {
      if (!field.digitNormalize) continue
      const value = digitsOnly(field.getValue(item))
      if (value && value.includes(dSearch)) return true
    }
  }
  return false
}

export function formatConfiguredLabel(
  item: any,
  type: DropdownTypeKey,
  fieldKeys: string[],
  language: string,
  options?: { separator?: string; skipEmpty?: boolean }
): string {
  const sep = options?.separator ?? ' · '
  const skipEmpty = options?.skipEmpty ?? true
  const fields = fieldDefsFor(type, fieldKeys)
  const parts: string[] = []

  for (const field of fields) {
    let value = resolveFieldValue(field, item, language)
    if (field.key === 'rate' && value) {
      value = value.includes('%') ? value : `${value}%`
    }
    if (!value && skipEmpty) continue
    parts.push(value)
  }

  return parts.join(sep)
}

export function formatConfiguredParts(
  item: any,
  type: DropdownTypeKey,
  fieldKeys: string[],
  language: string
): { primary: string; secondary: string[] } {
  const fields = fieldDefsFor(type, fieldKeys)
  const values: string[] = []
  for (const field of fields) {
    let value = resolveFieldValue(field, item, language)
    if (field.key === 'rate' && value && !value.includes('%')) value = `${value}%`
    if (value) values.push(value)
  }
  return {
    primary: values[0] || '',
    secondary: values.slice(1),
  }
}
